import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import Footer from './Footer';
import { useDashboardIdentity } from '@/lib/auth/dashboard-context';
import './dashboard-theme.css';

interface MasterLayoutProps {
  /** Page content. When omitted, the matched child route is rendered (<Outlet />). */
  children?: ReactNode;
}

/** Dashboard shell: sidebar + header + page content + footer. */
export default function DashboardMasterLayout({ children }: MasterLayoutProps) {
  const identity = useDashboardIdentity();
  const mainRef = useRef<HTMLElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('loyalty_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('loyalty_sidebar_collapsed', String(next));
      } catch {
        /* storage unavailable (private mode): keep the in-memory value */
      }
      return next;
    });
  };

  const scrollContentToTop = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    mainRef.current?.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });
  };

  // Keyboard shortcut: Ctrl + B or Cmd + B to toggle sidebar collapse
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        toggleCollapse();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="dash-bi h-screen w-screen overflow-hidden flex relative">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
        />
        <div className="flex-1 min-w-0 h-screen flex flex-col overflow-hidden">
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 pt-6 pb-28 lg:pb-6 focus:outline-none flex flex-col justify-between">
            <fieldset disabled={identity?.dashboardReadOnly ?? true} className="m-0 min-w-0 border-0 p-0">
              {children ?? <Outlet />}
            </fieldset>
            <Footer />
          </main>
        </div>
        <MobileNav onNavigate={scrollContentToTop} />
    </div>
  );
}
